import { useState, useEffect, useCallback } from 'react';

// Standardized breakpoints — single source of truth
export const BREAKPOINTS = {
  mobile: 0,      // 0–639px
  tablet: 640,    // 640–1023px
  desktop: 1024,  // 1024–1439px
  wide: 1440,     // 1440px+
};

// matchMedia queries (once, reusable)
const QUERIES = {
  tablet: `(min-width: ${BREAKPOINTS.tablet}px)`,
  desktop: `(min-width: ${BREAKPOINTS.desktop}px)`,
  wide: `(min-width: ${BREAKPOINTS.wide}px)`,
  portrait: '(orientation: portrait)',
  touch: '(pointer: coarse)',
  hover: '(hover: hover)',
};

function getDevice(mqResults) {
  const isWide = mqResults.wide;
  const isDesktop = mqResults.desktop;
  const isTablet = mqResults.tablet && !mqResults.desktop;
  const isMobile = !mqResults.tablet;

  let breakpoint = 'mobile';
  if (isWide) breakpoint = 'wide';
  else if (isDesktop) breakpoint = 'desktop';
  else if (isTablet) breakpoint = 'tablet';

  return {
    isMobile,
    isTablet,
    isDesktop: isDesktop || isWide,
    isWide,
    breakpoint,
    isPortrait: mqResults.portrait,
    isTouch: mqResults.touch,
    canHover: mqResults.hover,
  };
}

export function useDevice() {
  const [device, setDevice] = useState(() => {
    // SSR-safe initial value
    if (typeof window === 'undefined') {
      return {
        isMobile: false, isTablet: false, isDesktop: true, isWide: false,
        breakpoint: 'desktop', isPortrait: false, isTouch: false, canHover: true,
      };
    }
    const results = {};
    for (const [key, query] of Object.entries(QUERIES)) {
      results[key] = window.matchMedia(query).matches;
    }
    return getDevice(results);
  });

  useEffect(() => {
    const mediaQueryLists = {};
    const results = {};

    const handleChange = () => {
      for (const key in mediaQueryLists) {
        results[key] = mediaQueryLists[key].matches;
      }
      setDevice(getDevice(results));
    };

    for (const [key, query] of Object.entries(QUERIES)) {
      const mql = window.matchMedia(query);
      mediaQueryLists[key] = mql;
      results[key] = mql.matches;
      mql.addEventListener('change', handleChange);
    }

    // Set initial
    setDevice(getDevice(results));

    return () => {
      for (const mql of Object.values(mediaQueryLists)) {
        mql.removeEventListener('change', handleChange);
      }
    };
  }, []);

  // Utility: run different logic per breakpoint
  const match = useCallback((config) => {
    const { breakpoint } = device;
    if (config[breakpoint] !== undefined) return config[breakpoint];
    // Fallback chain: wide->desktop->tablet->mobile
    if (breakpoint === 'wide' && config.desktop !== undefined) return config.desktop;
    if ((breakpoint === 'wide' || breakpoint === 'desktop') && config.tablet !== undefined) return config.tablet;
    return config.mobile ?? config.default;
  }, [device]);

  return { ...device, match };
}
